export const JOB_SUCCESS_STATUS = 5;

export const isJobSuccessStatus = (record = {}) => record.current_status === JOB_SUCCESS_STATUS;

export const formatAnalysisValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return "--";
    }
    return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatEmiBounceValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return "--";
    }
    return num.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    });
};

// Currency (full-rupee) analysis fields — only these are compacted to L/Cr when
// the compact toggle is on. Percentages / counts are shown as-is in both modes.
export const CURRENCY_ANALYSIS_FIELDS = new Set([
    "monthlyAvgBalance",
    "monthlyAvgBalance_6m",
    "monthlyDebitRunningAvgWithReturns",
    "monthlyCreditRunningAvgWithReturns",
    "credit_limit",
    "avgMonthlyDebit_complete",
    "avgMonthlyDebit_6m",
    "totalMonthlyDebit_complete",
    "totalMonthlyDebit_6m",
    "avgMonthlyCredit_complete",
    "avgMonthlyCredit_6m",
    "totalMonthlyCredit_complete",
    "totalMonthlyCredit_6m",
]);

// Compact rupee value: >=1 Cr -> "X Cr", >=1 Lakh -> "X L", else the full
// grouped number (no ₹ symbol, matching the non-compact display).
// When `lakhsOnly` is set, values are never rolled up to Cr — everything at or
// above 1 Lakh is shown in Lakhs (e.g. 2 Cr -> "200 L").
export const formatCompactAnalysisValue = (value, lakhsOnly = false) => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return "--";
    }
    const abs = Math.abs(num);
    if (!lakhsOnly && abs >= 10000000) {
        return (
            (num / 10000000).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
            }) + " Cr"
        );
    }
    if (abs >= 100000) {
        return (
            (num / 100000).toLocaleString("en-IN", {
                maximumFractionDigits: 2,
            }) + " L"
        );
    }
    return formatAnalysisValue(num);
};

export const getAnalysisFieldDisplayValue = (
    key,
    analysisMap,
    isLoading,
    compact = false,
    lakhsOnly = false
) => {
    if (analysisMap[key] == null) {
        return isLoading ? "..." : "--";
    }
    if (key === "monthlyNumberOfContraEmi") {
        return formatEmiBounceValue(analysisMap[key]);
    }
    if (compact && CURRENCY_ANALYSIS_FIELDS.has(key)) {
        return formatCompactAnalysisValue(analysisMap[key], lakhsOnly);
    }
    return formatAnalysisValue(analysisMap[key]);
};

// Bounce-% formula mirrors the backend's own computation (inward/outward
// cheque bounces as a share of that window's debit/credit COUNT, not
// amount) — see e.g. `(inwChqBounces / debits) * 100` server-side. Returns 0
// (not null) when the count itself is a real 0, matching that.
const percentOf = (numerator, denominator) =>
    denominator ? (numerator / denominator) * 100 : 0;

// `windowMode`:
//   "complete" (default) — all-transaction windows
//     (summaryWindows.normal.average_* / total_*).
//   "credit" — the parallel credit-only windows the backend also sends
//     (summaryWindows.credit.average_* / total_*) — same shape, just scoped
//     to credit-side transactions ("credit exclusion"). Now drives every
//     windowed metric — ABB, Inward/Outward Cheque Bounce %, No EMI bounce,
//     and the four debit/credit AV./Total fields — since the raw counts
//     those are computed from (balAvg, inwChqBounces/outwChqBounces/debits/
//     credits, inwEMIBounces) live inside BOTH the normal and credit window
//     groups. Credit Limit and Fraud Count are the only two with no
//     window-scoped source at all, so they stay fixed regardless of the
//     toggle. When a window/field is missing from a payload, that metric
//     falls back to whatever legacy top-level field exists (complete mode
//     only — there's no credit-only legacy field to fall back to), and if
//     nothing is available at all it reads null (renders "--") rather than
//     silently showing the wrong window's number.
//
// The backend has shipped this as TWO different shapes over time:
//   - current:  summaryWindows.normal.{average,total}_{6m,12m,complete}
//               summaryWindows.credit.{average,total}_{6m,12m,complete}
//   - legacy:   summaryWindows.{average,total}_{6m,12m,complete}           (complete mode)
//               summaryWindows.credit_{average,total}_{6m,12m,complete}    (credit mode)
// Both are supported — nested group first, flat/prefixed as a fallback —
// since older stored analyses were computed under the legacy shape.
export const getNormalizedAnalysisMap = (stats, windowMode = "complete") => {
    if (!stats) {
        return {};
    }
    const isCredit = windowMode === "credit";
    const windows = stats.summaryWindows || {};
    const group = windows[isCredit ? "credit" : "normal"] || {};
    const flatPrefix = isCredit ? "credit_" : "";
    // "complete" is the backend's full-history window. Older non-credit
    // payloads only ever had *_12m, so that's the fallback for "complete"
    // mode; "credit" mode has no such legacy fallback to fall back to.
    const avgComplete =
        group.average_complete
        || windows[`${flatPrefix}average_complete`]
        || (!isCredit && (group.average_12m || windows.average_12m))
        || {};
    const avg6m =
        group.average_6m
        || windows[`${flatPrefix}average_6m`]
        || {};
    const totalComplete =
        group.total_complete
        || windows[`${flatPrefix}total_complete`]
        || (!isCredit && (group.total_12m || windows.total_12m))
        || {};
    const total6m =
        group.total_6m
        || windows[`${flatPrefix}total_6m`]
        || {};
    return {
        // ABB — that window's average balance (balAvg). Falls back to the
        // legacy flat fields only in "complete" mode.
        monthlyAvgBalance:
            avgComplete.balAvg
            ?? (isCredit ? null : stats.monthlyAvgBalance_complete ?? stats.monthlyAvgBalance_12m ?? stats.monthlyAvgBalance),
        monthlyAvgBalance_6m:
            avg6m.balAvg ?? (isCredit ? null : stats.monthlyAvgBalance_6m),
        // Inward/Outward Cheque Bounce (%) — bounced count ÷ debit/credit
        // count for that window, ×100 (mirrors the backend's own formula).
        // Computed only when the window actually has a `debits`/`credits`
        // count to divide by; otherwise falls through to the legacy chain
        // (complete mode) or null (credit mode).
        sixMonthAvgInwardBouncingPercent:
            (avgComplete.debits != null ? percentOf(avgComplete.inwChqBounces, avgComplete.debits) : null)
            ?? (isCredit ? null : stats.inwardBouncingPercent_complete
                ?? stats.inwardBouncingPercent_12m
                ?? stats.overallAvgInwardBouncingPercent
                ?? stats.sixMonthAvgInwardBouncingPercent
                ?? stats.inward_cheque_bounce),
        inwardBouncingPercent_6m:
            (avg6m.debits != null ? percentOf(avg6m.inwChqBounces, avg6m.debits) : null)
            ?? (isCredit ? null : stats.inwardBouncingPercent_6m),
        sixMonthAvgOutwardBouncingPercent:
            (avgComplete.credits != null ? percentOf(avgComplete.outwChqBounces, avgComplete.credits) : null)
            ?? (isCredit ? null : stats.outwardBouncingPercent_complete
                ?? stats.outwardBouncingPercent_12m
                ?? stats.overallAvgOutwardBouncingPercent
                ?? stats.sixMonthAvgOutwardBouncingPercent
                ?? stats.outward_cheque_bounce),
        outwardBouncingPercent_6m:
            (avg6m.credits != null ? percentOf(avg6m.outwChqBounces, avg6m.credits) : null)
            ?? (isCredit ? null : stats.outwardBouncingPercent_6m),
        // No EMI bounce — a COUNT, so it comes from the total (not average)
        // window, mirroring the backend's own `total_no_emi_bounce` source.
        monthlyNumberOfContraEmi:
            totalComplete.inwEMIBounces
            ?? (isCredit ? null : stats.total_no_emi_bounce_complete
                ?? stats.total_no_emi_bounce_12m
                ?? stats.total_no_emi_bounce
                ?? stats.monthlyNumberOfContraEmi
                ?? stats.no_emi_bounce),
        monthlyNumberOfContraEmi_6m:
            total6m.inwEMIBounces ?? (isCredit ? null : stats.total_no_emi_bounce_6m),
        // AV. monthly debit/credit — averaged-window figures, distinct from
        // the Total row below. Legacy top-level fallbacks only apply in
        // "complete" mode — there's no credit-only equivalent of them.
        avgMonthlyDebit_complete:
            avgComplete.totalDebit
            ?? (isCredit ? null : stats.monthlyDebitRunningAvg_complete ?? stats.monthlyDebitRunningAvg_12m),
        avgMonthlyDebit_6m:
            avg6m.totalDebit ?? (isCredit ? null : stats.monthlyDebitRunningAvg_6m),
        avgMonthlyCredit_complete:
            avgComplete.totalCredit
            ?? (isCredit ? null : stats.monthlyCreditRunningAvg_complete ?? stats.monthlyCreditRunningAvg_12m),
        avgMonthlyCredit_6m:
            avg6m.totalCredit ?? (isCredit ? null : stats.monthlyCreditRunningAvg_6m),
        // Total monthly debit/credit — summed-window figures, same
        // complete-history vs 6-month split as above.
        totalMonthlyDebit_complete: totalComplete.totalDebit,
        totalMonthlyDebit_6m: total6m.totalDebit,
        totalMonthlyCredit_complete: totalComplete.totalCredit,
        totalMonthlyCredit_6m: total6m.totalCredit,
        credit_limit: stats.credit_limit,
    };
};

/**
 * Pulls the fraud section out of an analysis payload (the normalized statement,
 * or a job row's `ui_response` — both carry the same shape).
 *
 * `indicators: null` means the analyser never returned the section (older
 * analyses omit it). That is deliberately distinct from an empty list: the cell
 * shows "--" for "never checked" and a 0 for "checked, nothing found".
 */
export const getFraudSummary = (stats) => {
    if (!stats || !Array.isArray(stats.fraud_indicators)) {
        return { count: null, indicators: null };
    }
    return {
        count:
            stats.fraud_identified_count
            ?? stats.fraud_indicators.filter((i) => i && i.identified).length,
        indicators: stats.fraud_indicators,
    };
};

export const getSelectedStatementAnalysisIds = (jobList = []) =>
    jobList
        .filter((job) => isJobSuccessStatus(job) && job.using)
        .map((job) => job.analysis_request);

export const NORMALIZED_STATEMENT_EDIT_FIELD_MAP = {
    sixMonthAvgInwardBouncingPercent: "inward_cheque_bounce",
    sixMonthAvgOutwardBouncingPercent: "outward_cheque_bounce",
    monthlyNumberOfContraEmi: "no_emi_bounce",
    credit_limit: "credit_limit",
};

export const formatNormalizedStatementPatchValue = (value) => String(value).trim();

// Fields an authorised user may override in the summary header. Fraud Count is
// deliberately absent: it is derived from the indicator list, so a hand-typed
// count would contradict the breakdown shown on hover.
export const EDITABLE_ANALYSIS_FIELDS = [
    "sixMonthAvgInwardBouncingPercent",
    "sixMonthAvgOutwardBouncingPercent",
    "monthlyNumberOfContraEmi",
    "credit_limit",
];

// Credit Manager — group id 7 on `user.data.groups`. Distinct from
// `stage_admin` containing 7: stage_admin is "admins the credit appraisal
// STAGE", the group is the user's ROLE. A Credit Manager who is not a stage
// admin still owns these numbers, so either one grants the override.
export const isCreditManagerGroup = (userGroup) =>
    !!(userGroup && userGroup.some && userGroup.some(_ => _ && Number(_.id) === 7));

export const isEditableAnalysisField = (fieldKey, adminGroup, recreditViewOnlyPermission, userGroup) =>
    (adminGroup?.includes(7) || isCreditManagerGroup(userGroup))
    && EDITABLE_ANALYSIS_FIELDS.includes(fieldKey)
    && !recreditViewOnlyPermission;

// Header renders as a proper 2-D grid: one COLUMN per metric family, each
// holding an ordered stack of ROWS (secondary window(s) on top, the
// complete-history/base figure last). Today every column has exactly 2 rows;
// the shape is N-rows-per-column on purpose so a future secondary window
// (12m, credit exclusion, …) is just another entry appended to `rows` — no
// renderer change needed, and the grid still lines up because every row
// index across every column occupies the same physical grid row (see
// BankStatementAnalysisSummary, which renders row-major so heights stay
// synced instead of each column free-floating its own stack).
//
// Row shape: { key, label, formula, small }
//   key     — analysisMap lookup key AND the editable-field lookup key (see
//             isEditableAnalysisField — only rows whose key is in
//             EDITABLE_ANALYSIS_FIELDS ever show the edit pencil).
//   small   — visual scale: true = sub-row (14px), false = headline (18px).
//
// Fraud Count and Credit Limit are NOT isolated from this system — Credit
// Limit is a normal column, and because it has no secondary window, Fraud
// Count fills that column's second row instead of getting its own column
// (see `fraud: true` below, read by the renderer to swap in
// FraudIndicatorsCell instead of EditableAnalysisField for that one row).
export const getBankStatementAnalysisList = (bankCode = "") => {
    const monthsLabel = bankCode === "KSF" ? "3" : "6";
    return [
        {
            id: "monthlyAvgBalance",
            rows: [
                {
                    key: "monthlyAvgBalance_6m",
                    label: "ABB (6m)",
                    formula: "Average Monthly Balance, last 6 months",
                    small: false,
                },
                {
                    key: "monthlyAvgBalance",
                    label: "ABB",
                    formula: "Average Monthly Balance (complete history)",
                    small: true,
                },
            ],
        },
        {
            id: "avgMonthlyDebit",
            rows: [
                {
                    key: "avgMonthlyDebit_6m",
                    label: `AV. monthly debit ${monthsLabel}m`,
                    formula: `Average monthly debits of last ${monthsLabel} months`,
                    small: false,
                },
                {
                    key: "avgMonthlyDebit_complete",
                    label: "AV. monthly debit",
                    formula: "Average monthly debits (complete history)",
                    small: true,
                },
            ],
        },
        {
            id: "totalMonthlyDebit",
            rows: [
                {
                    key: "totalMonthlyDebit_6m",
                    label: `Total monthly debit ${monthsLabel}m`,
                    formula: `Total monthly debits of last ${monthsLabel} months`,
                    small: false,
                },
                {
                    key: "totalMonthlyDebit_complete",
                    label: "Total monthly debit",
                    formula: "Total monthly debits (complete history)",
                    small: true,
                },
            ],
        },
        {
            id: "avgMonthlyCredit",
            rows: [
                {
                    key: "avgMonthlyCredit_6m",
                    label: `AV. monthly credit ${monthsLabel}m`,
                    formula: `Average monthly credits of last ${monthsLabel} months`,
                    small: false,
                },
                {
                    key: "avgMonthlyCredit_complete",
                    label: "AV. monthly credit",
                    formula: "Average monthly credits (complete history)",
                    small: true,
                },
            ],
        },
        {
            id: "totalMonthlyCredit",
            rows: [
                {
                    key: "totalMonthlyCredit_6m",
                    label: `Total monthly credit ${monthsLabel}m`,
                    formula: `Total monthly credits of last ${monthsLabel} months`,
                    small: false,
                },
                {
                    key: "totalMonthlyCredit_complete",
                    label: "Total monthly credit",
                    formula: "Total monthly credits (complete history)",
                    small: true,
                },
            ],
        },
        {
            id: "inwardBouncingPercent",
            rows: [
                {
                    key: "inwardBouncingPercent_6m",
                    label: "Inward Cheque Bounce (%)",
                    formula: "No. of Inward cheque bounced / Total no. of cheque recieved, last 6 months",
                    small: false,
                },
                {
                    key: "sixMonthAvgInwardBouncingPercent",
                    label: "Inward Cheque Bounce (%)",
                    formula: "No. of Inward cheque bounced / Total no. of cheque recieved",
                    small: true,
                },
            ],
        },
        {
            id: "outwardBouncingPercent",
            rows: [
                {
                    key: "outwardBouncingPercent_6m",
                    label: "Outward Cheque Bounce (%)",
                    formula: "No. of Outward cheque bounced / Total no. of cheque issued, last 6 months",
                    small: false,
                },
                {
                    key: "sixMonthAvgOutwardBouncingPercent",
                    label: "Outward Cheque Bounce (%)",
                    formula: "No. of Outward cheque bounced / Total no. of cheque issued",
                    small: true,
                },
            ],
        },
        {
            id: "monthlyNumberOfContraEmi",
            rows: [
                {
                    key: "monthlyNumberOfContraEmi_6m",
                    label: "No EMI bounce",
                    formula: null,
                    small: false,
                },
                {
                    key: "monthlyNumberOfContraEmi",
                    label: "No EMI bounce",
                    formula: null,
                    small: true,
                },
            ],
        },
        {
            id: "credit_limit",
            rows: [
                // Fraud Count and Credit Limit are bound into one column
                // (Credit Limit has no secondary window to pair with), but
                // they are EQUALS, not a headline + secondary pair — both
                // stay full-size/header-styled (small: false); only the
                // divider between them comes from row position, not size.
                {
                    key: "fraud_count",
                    label: "Fraud Count",
                    formula: null,
                    small: false,
                    fraud: true,
                },
                {
                    key: "credit_limit",
                    label: "Credit Limit",
                    formula: null,
                    small: false,
                },
            ],
        },
    ];
};

export const mergeNormalizedStatementPatch = (stats = {}, apiField, value) => {
    const nextStats = { ...stats, [apiField]: value };
    if (apiField === "inward_cheque_bounce") {
        nextStats.sixMonthAvgInwardBouncingPercent = value;
        nextStats.overallAvgInwardBouncingPercent = value;
    }
    if (apiField === "outward_cheque_bounce") {
        nextStats.sixMonthAvgOutwardBouncingPercent = value;
        nextStats.overallAvgOutwardBouncingPercent = value;
    }
    if (apiField === "no_emi_bounce") {
        nextStats.total_no_emi_bounce = value;
        nextStats.monthlyNumberOfContraEmi = value;
    }
    return nextStats;
};
